// auditpm-v6
import React, { useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { AUDIT_TYPES, SHMS_SCOPES, resolveScope, resolveScopeCategory } from '../lib/supabase'
import { workflowState, displayState } from '../lib/workflow'
import { portalListLabel } from '../lib/bizsafePortal'
import { format, parseISO } from 'date-fns'
import ExpiryBadge from '../components/ExpiryBadge'
import { resolveExpiry } from '../lib/expiry'
import { unresolvedHighRiskCoiNames } from '../lib/coi'
import { isIA, isOTE, iaJobStatus, oteJobStatus, oteFinished, serviceOf, standardsLabel } from '../lib/service'
import ServiceSwitch from '../components/ServiceSwitch'
import { JOBS_LIST_COLUMNS } from '../lib/jobLists'
import { toast } from '../lib/toast'

const STATUS_BADGE = {
  'Enquiry': 'badge-gray', 'Questionnaire sent': 'badge-gray',
  'Questionnaire received': 'badge-info', 'Quotation draft': 'badge-info',
  'Quotation sent': 'badge-warn', 'Confirmed': 'badge-info',
  'Awaiting scheduling': 'badge-warn',
  'Scheduled': 'badge-info', 'In progress': 'badge-warn',
  'Completed': 'badge-ok', 'Cancelled': 'badge-gray',
  // Post-submission workflow states
  'Pending Office Review': 'badge-warn', 'Returned for Revision': 'badge-danger',
  'Pending WSHC Outcome': 'badge-info',
  // Internal Audit job states
  'Plan not sent': 'badge-warn', 'Audit in progress': 'badge-warn', 'Returned to auditor': 'badge-danger',
  'Awaiting payment': 'badge-warn', 'Sending report': 'badge-info', 'Awaiting close-out': 'badge-warn',
  // New WSHC client-portal process (lib/bizsafePortal.js portalListLabel)
  'Client Acknowledged': 'badge-info', 'Ended – no certificate': 'badge-gray',
  // OTE job stages (lib/service.js OTE_STAGE_LABEL)
  'Confirmation sent': 'badge-info', 'Job in progress': 'badge-warn', 'Waiting for client': 'badge-warn', 'Signing': 'badge-info',
  'Ready to send endorsement': 'badge-warn', 'Sending endorsement': 'badge-info', 'Endorsement sent': 'badge-ok',
  'Not endorsed': 'badge-danger', 'Closed — not endorsed': 'badge-gray', 'Void': 'badge-gray',
}

// Operational job-stage label for the list. Keeps quotation-stage wording (e.g. "Signed") out of Jobs:
// a confirmed/scheduled job with no audit date is "Awaiting scheduling"; once a date is set it is "Scheduled".
// Terminal/other statuses (Completed, Cancelled, In progress) are shown as-is.
function jobStatusLabel(j) {
  if (isOTE(j.audit_type)) return oteJobStatus(j)
  if (isIA(j.audit_type)) return iaJobStatus(j)
  // Once the audit package enters the post-submission workflow, that lean state is the meaningful status
  // (never the vague "In progress"). Pre-audit jobs fall through to the pipeline wording.
  // New WSHC client-portal process: its own steps (the client applies on WSHC themselves).
  const portal = portalListLabel(j)
  if (portal) return portal
  const ws = workflowState(j)
  if (ws) return displayState(j)
  if (j.status === 'Confirmed' || j.status === 'Scheduled') {
    return j.audit_date ? 'Scheduled' : 'Awaiting scheduling'
  }
  return j.status
}

// ── Sorting ──────────────────────────────────────────────────────────────────────────────────────────
// Parse an ISO date (or null) to epoch ms; invalid/absent → null (always sorted last).
function msOf(dateStr) {
  if (!dateStr) return null
  try { const t = parseISO(String(dateStr)).getTime(); return Number.isNaN(t) ? null : t } catch (e) { return null }
}

// DEFAULT SORT = most recently CREATED first (latest job on top), across every filter. Clicking a column header
// still overrides this with that column's sort (see manualCompare). Rows with no created_at sort last.
function defaultCompare(a, b) {
  const ma = msOf(a.created_at), mb = msOf(b.created_at)
  if (ma == null && mb == null) return 0
  if (ma == null) return 1
  if (mb == null) return -1
  return mb - ma                                         // newest first
}

// Manual column sort (header click). Missing values ALWAYS sort last, regardless of direction.
function manualCompare(col, dir) {
  return (a, b) => {
    if (col === 'client') {
      const va = (a.clients?.name || '').toLowerCase(), vb = (b.clients?.name || '').toLowerCase()
      if (!va && !vb) return 0; if (!va) return 1; if (!vb) return -1
      return dir === 'desc' ? vb.localeCompare(va) : va.localeCompare(vb)
    }
    const va = col === 'audit' ? msOf(a.audit_date) : msOf(resolveExpiry(a.clients, a.form_data))
    const vb = col === 'audit' ? msOf(b.audit_date) : msOf(resolveExpiry(b.clients, b.form_data))
    if (va == null && vb == null) return 0
    if (va == null) return 1
    if (vb == null) return -1
    return dir === 'desc' ? vb - va : va - vb
  }
}

// ia = opened from the Internal Audit view: the job is a client internal audit (linked to the client's internal audit
// record automatically). The bizSAFE view never offers the internal audit type.
function NewJobModal({ onClose, onCreated, ia }) {
  const [clients, setClients] = useState([])
  const [form, setForm] = useState({ client_id: '', audit_type: ia ? AUDIT_TYPES.ISO : '', scope: '', notes: '' })
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    supabase.from('clients').select('id,name').order('name').then(({ data }) => setClients(data || []))
  }, [])

  function set(k, v) { setForm(f => ({ ...f, [k]: v })) }

  async function save() {
    setSaving(true)
    // A JOB is committed work — enquiries live in Quotations/Pipeline, never here. Manually-created jobs (client
    // signed outside AuditPM) start CONFIRMED, exactly like a job created from a signed quotation; the display
    // then derives "Awaiting scheduling" (no audit date) → "Scheduled" (date set). Previously these defaulted to
    // 'Enquiry' and sat there mislabelled until the audit plan was sent.
    const { data, error } = await supabase.from('jobs')
      .insert({ ...form, status: 'Confirmed' })
      .select('*, clients(name)').maybeSingle()
    setSaving(false)
    if (!error) onCreated(data)
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{ia ? 'New internal audit job' : 'New job'}</h2>
          <button onClick={onClose} style={{ border: 'none', background: 'none', fontSize: 18, cursor: 'pointer', color: 'var(--text3)' }}>×</button>
        </div>
        <div className="field">
          <label>Client *</label>
          <select value={form.client_id} onChange={e => set('client_id', e.target.value)}>
            <option value="">Select client…</option>
            {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        {!ia && <div className="field">
          <label>Audit type *</label>
          <select value={form.audit_type} onChange={e => set('audit_type', e.target.value)}>
            <option value="">Select…</option>
            {Object.values(AUDIT_TYPES).filter(t => !isIA(t)).map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>}
        {form.audit_type === AUDIT_TYPES.SHMS && (
          <div className="field">
            <label>SHMS scope</label>
            <select value={form.scope} onChange={e => set('scope', e.target.value)}>
              <option value="">Select scope…</option>
              {SHMS_SCOPES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        )}
        <div className="field"><label>Notes</label><textarea value={form.notes} onChange={e => set('notes', e.target.value)} rows={2} /></div>
        <div className="modal-footer">
          <button onClick={onClose}>Cancel</button>
          <button className="primary" onClick={save} disabled={!form.client_id || !form.audit_type || saving}>{saving ? 'Creating…' : 'Create job'}</button>
        </div>
      </div>
    </div>
  )
}

// Reference guide mapping — resolved via the SCOPE_REGISTRY (handles aliases / legacy labels), keyed by
// the SHMS sub-scope when present, else the audit type.
function getRefGuide(auditType, scope) {
  // Resolve consistently with the list's category filter: for bizSAFE RM the reference guide is ALWAYS the RM
  // Code of Practice — the free-text scope must not flip it to a SHMS guide (e.g. a stray "ConSASS" in scope).
  // Only SHMS audits use the sub-scope to distinguish ConSASS / SS 651 / Marine.
  const cat = resolveScopeCategory(auditType) || resolveScopeCategory(scope)
  const entry = cat === 'BIZSAFE'
    ? (resolveScope(auditType) || resolveScope(scope))
    : (resolveScope(scope) || resolveScope(auditType))
  if (!entry) return '—'
  switch (entry.id) {
    case 'S001': return 'Code of Practice on Workplace Safety and Health (WSH) Risk Management'
    case 'S002': return 'Requirements for the Construction Safety Audit Scoring System (ConSASS)'
    case 'S003': return 'Requirements on the Implementation of Safety Management System for the Metalworking Industry'
    case 'S004': return 'Workplace Safety & Health Requirements for Marine Industries'
    case 'S005': case 'S006': case 'S007': case 'S008': return 'SS 651:2019'
    default: return '—'
  }
}

function SACReportModal({ jobs, onClose }) {
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [reportType, setReportType] = useState('shms') // 'shms' or 'rm'

  // Combined audits carry ONE combined man-day (SAC Option A), recorded on the primary job. Attribute it to the
  // primary once; linked members reference it so the "Total Audit Man-Day" column can't multiply a single
  // combined session (e.g. 3 companies audited together must not read as 3 × 0.5).
  const primaryClientByGroup = {}
  ;(jobs || []).forEach(j => { if (j.combined_group_id && j.is_combined_primary) primaryClientByGroup[j.combined_group_id] = j.clients?.name || 'the primary company' })
  const isLinkedMember = (j) => !!(j.combined_group_id && !j.is_combined_primary)
  const mandayFull = (j) => isLinkedMember(j)
    ? `Combined audit with ${primaryClientByGroup[j.combined_group_id] || 'the primary company'}`
    : (j.mandays || '—')

  // Project name: blank (not a dash) when nothing is recorded — many audits legitimately have no project, and
  // a stored placeholder dash should read as empty on the SAC list.
  const projectName = (j) => {
    const p = String(j.project_name || '').trim()
    return (p === '' || p === '-' || p === '–' || p === '—') ? '' : p
  }

  // "Done" = the audit is finalised: completed AND its bizSAFE expiry recorded. auditor_closed is set — by BOTH
  // finalisation paths (the Finalise RPC and the client-expiry trigger) — at the moment the new bizSAFE expiry
  // is recorded; wshc_new_expiry is the RPC's per-job recorded expiry. Anything not yet finalised is "In Progress".
  const statusLabel = (j) =>
    (j.auditor_closed === true || (j.status === 'Completed' && !!j.wshc_new_expiry)) ? 'Done' : 'In Progress'

  function getFiltered() {
    return jobs.filter(j => {
      if (!j.audit_date || j.status === 'Cancelled') return false   // a cancelled audit never goes to SAC
      if (dateFrom && j.audit_date < dateFrom) return false
      if (dateTo && j.audit_date > dateTo) return false
      // Registry category (handles aliases): audit_type first, else the SHMS sub-scope.
      const cat = resolveScopeCategory(j.audit_type) || resolveScopeCategory(j.scope)
      if (reportType === 'rm') return cat === 'BIZSAFE'
      if (reportType === 'shms') return cat === 'SHMS'
      return true
    }).sort((a, b) => a.audit_date > b.audit_date ? 1 : -1)
  }

  function download() {
    const rows = getFiltered()
    if (rows.length === 0) { toast('Nothing matches these filters', 'info'); return }

    const title = reportType === 'rm' ? 'bizSAFE Risk Management Audit List' : 'Mandatory SHMS Audit List'
    const headers = ['S/N', 'Workplace No.', 'Organisation Audited', 'Project Name', 'Site Audited', 'Date of Audit', 'Reference Guide', 'Name of Auditors', 'Total Audit Man-Day', 'Status']

    const csvRows = [
      [title],
      [`Period: ${dateFrom || 'All'} to ${dateTo || 'All'}`],
      [],
      headers,
      ...rows.map((j, i) => {
        const auditors = [j.auditor_name, j.auditor2_name].filter(Boolean).join(', ')
        const refGuide = getRefGuide(j.audit_type, j.scope)
        const workplaceNo = j.clients?.uen || '—'
        return [
          i + 1,
          workplaceNo,
          j.clients?.name || '—',
          projectName(j),
          j.audit_site_address || '—',
          j.audit_date || '—',
          refGuide,
          auditors || '—',
          mandayFull(j),
          statusLabel(j),
        ]
      }),
      [],
      [`Total records: ${rows.length}`],
    ]

    // Lead with a UTF-8 BOM so Excel opens the CSV as UTF-8 (otherwise em-dashes and other non-ASCII render as "â€”").
    const csv = '\uFEFF' + csvRows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `SAC_Audit_List_${reportType.toUpperCase()}_${dateFrom || 'all'}_to_${dateTo || 'all'}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  const preview = getFiltered()

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 680 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Generate SAC audit list</h2>
          <button onClick={onClose} style={{ border: 'none', background: 'none', fontSize: 18, cursor: 'pointer', color: 'var(--text3)' }}>×</button>
        </div>
        <p style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 16 }}>
          Filter by date range and audit type, then download as CSV for SAC submission.
        </p>

        <div className="grid-2" style={{ marginBottom: 14 }}>
          <div className="field"><label>Date from</label>
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} /></div>
          <div className="field"><label>Date to</label>
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} /></div>
        </div>

        <div className="field" style={{ marginBottom: 16 }}>
          <label>Report type</label>
          <div style={{ display: 'flex', gap: 10, marginTop: 6 }}>
            {[['shms', 'Mandatory audits (SHMS)'], ['rm', 'WSH RM Audit (bizSAFE)']].map(([v, l]) => (
              <label key={v} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer',
                padding: '8px 14px', borderRadius: 8,
                background: reportType === v ? 'var(--brand-light)' : 'var(--bg2)',
                border: `0.5px solid ${reportType === v ? 'var(--brand)' : 'var(--border)'}`,
                color: reportType === v ? 'var(--brand-dark)' : 'var(--text2)' }}>
                <input type="radio" checked={reportType === v} onChange={() => setReportType(v)} style={{ display: 'none' }} />
                {l}
              </label>
            ))}
          </div>
        </div>

        {/* Preview */}
        <div style={{ background: 'var(--bg2)', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13 }}>
          <div style={{ fontWeight: 500, marginBottom: 6, color: 'var(--text2)' }}>Preview — {preview.length} record{preview.length !== 1 ? 's' : ''}</div>
          {preview.length > 0 ? (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border)' }}>
                    {['S/N', 'Workplace No.', 'Organisation', 'Project', 'Site', 'Date', 'Auditors', 'Days', 'Status'].map(h => (
                      <th key={h} style={{ textAlign: 'left', padding: '4px 6px', fontWeight: 500, color: 'var(--text2)', whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {preview.slice(0, 5).map((j, i) => (
                    <tr key={j.id} style={{ borderBottom: '1px solid var(--border)' }}>
                      <td style={{ padding: '4px 6px' }}>{i + 1}</td>
                      <td style={{ padding: '4px 6px' }}>{j.clients?.uen || '—'}</td>
                      <td style={{ padding: '4px 6px', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{j.clients?.name || '—'}</td>
                      <td style={{ padding: '4px 6px', color: 'var(--text3)' }}>{projectName(j) || '—'}</td>
                      <td style={{ padding: '4px 6px', maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{j.audit_site_address || <span style={{ color: 'var(--text3)' }}>—</span>}</td>
                      <td style={{ padding: '4px 6px', whiteSpace: 'nowrap' }}>{j.audit_date || '—'}</td>
                      <td style={{ padding: '4px 6px' }}>{[j.auditor_name, j.auditor2_name].filter(Boolean).join(', ') || '—'}</td>
                      <td style={{ padding: '4px 6px' }}>{isLinkedMember(j) ? 'Combined' : (j.mandays || '—')}</td>
                      <td style={{ padding: '4px 6px', whiteSpace: 'nowrap' }}>{statusLabel(j)}</td>
                    </tr>
                  ))}
                  {preview.length > 5 && (
                    <tr><td colSpan={9} style={{ padding: '4px 6px', color: 'var(--text3)', fontStyle: 'italic' }}>…and {preview.length - 5} more</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{ color: 'var(--text3)', fontStyle: 'italic' }}>No records match the selected filters.</div>
          )}
        </div>

        <div className="modal-footer">
          <button onClick={onClose}>Cancel</button>
          <button className="primary" onClick={download} disabled={preview.length === 0}>
            Download CSV ({preview.length} records)
          </button>
        </div>
      </div>
    </div>
  )
}

export default function Jobs() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [jobs, setJobs] = useState([])
  const [search, setSearch] = useState('')
  const [filterType, setFilterType] = useState('')
  // Deep-links from the Dashboard pass ?status=<displayState> (e.g. "Pending Office Review") — the SAME
  // vocabulary as the status column and the dropdown, so count / filter / column always agree.
  const [filterStatus, setFilterStatus] = useState(searchParams.get('status') || '')
  // Tabs: Active (default, day-to-day) | Completed (audit evidence, with year filter) | All (everything,
  // incl. Cancelled). A ?status= deep-link opens on All so the linked rows are never hidden by the tab.
  const [tab, setTab] = useState(searchParams.get('status') ? 'all' : 'active')
  const [yearFilter, setYearFilter] = useState('')
  const [showNew, setShowNew] = useState(false)
  const [showSAC, setShowSAC] = useState(false)
  const [loading, setLoading] = useState(true)
  // Sort override: null col → the default next-deadline sort; a col → manual sort (click a header). Clicking
  // the same header cycles asc → desc → back to default, so office can always return to the smart default.
  const [sortCol, setSortCol] = useState(null)
  const [sortDir, setSortDir] = useState('asc')
  const [coiRows, setCoiRows] = useState([])
  const [coiDecs, setCoiDecs] = useState([])
  // [bizSAFE | Internal Audit] — bizSAFE first; Internal Audit only filters the jobs already loaded (+ the cycle
  // standards, fetched on the first switch).
  const [service, setService] = useState('biz')
  const [cycleStd, setCycleStd] = useState(null)
  const [pdfFailed, setPdfFailed] = useState(new Set())   // IA jobs whose latest report PDF could not be prepared

  useEffect(() => { loadJobs() }, [])
  useEffect(() => {
    if (service !== 'ia' || cycleStd) return
    supabase.from('client_ia_cycles').select('id, standards').then(({ data }) =>
      setCycleStd(Object.fromEntries((data || []).map(c => [c.id, c.standards || []]))))
    supabase.from('ia_reports').select('job_id, kind, version, status').then(({ data }) => {
      const latest = {}
      for (const r of data || []) { const k = `${r.job_id}|${r.kind}`; if (!latest[k] || r.version > latest[k].version) latest[k] = r }
      setPdfFailed(new Set(Object.values(latest).filter(r => r.status === 'error').map(r => r.job_id)))
    })
  }, [service, cycleStd])

  async function loadJobs() {
    const [{ data }, { data: cois }, { data: decs }] = await Promise.all([
      supabase.from('jobs').select(`${JOBS_LIST_COLUMNS}, clients(name, uen, address, bizsafe_expiry_date, industry, notes)`).order('created_at', { ascending: false }),
      supabase.from('auditor_coi_responses').select('job_id,auditor_slot,auditor_name,risk_level'),
      supabase.from('coi_decisions').select('job_id,auditor_slot,auditor_name,decision'),
    ])
    setJobs(data || [])
    setCoiRows(cois || [])
    setCoiDecs(decs || [])
    setLoading(false)
  }

  // Tab membership. SEARCH IS THE ESCAPE HATCH: typing a name searches ALL jobs regardless of the
  // active tab — so a completed (or cancelled) job can always be found by name, from anywhere.
  const ia = service === 'ia'
  const ote = service === 'ote'
  const other = ia || ote                                     // not bizSAFE: no SAC list, audit types or bizSAFE expiry
  // Each service tab shows only its own jobs: bizSAFE (incl. SHMS) · Internal Audit · OTE.
  const svcJobs = jobs.filter(j => serviceOf(j.audit_type) === (service === 'ia' ? 'ia' : service === 'ote' ? 'ote' : 'bizsafe'))
  const isDone = j => isOTE(j.audit_type) ? (oteFinished(j) && j.status !== 'Cancelled')
    : j.status === 'Completed' && !(isIA(j.audit_type) && ['returned', 'awaiting_closeout'].includes(j.ia_stage))
  const isCancelled = j => j.status === 'Cancelled'
  const activeCount = svcJobs.filter(j => !isDone(j) && !isCancelled(j)).length
  const completedCount = svcJobs.filter(isDone).length
  const inTab = j => {
    if (search) return true                                   // searching → look everywhere
    if (tab === 'active') return !isDone(j) && !isCancelled(j)
    if (tab === 'completed') {
      if (!isDone(j)) return false
      if (!yearFilter) return true
      const d = j.audit_date || j.created_at || ''
      return String(d).startsWith(yearFilter)
    }
    return true                                               // 'all' — incl. Cancelled
  }
  // Years present among completed jobs (by audit date), newest first — for the SAC evidence lookup.
  const completedYears = [...new Set(svcJobs.filter(isDone)
    .map(j => String(j.audit_date || j.created_at || '').slice(0, 4)).filter(y => /^\d{4}$/.test(y)))]
    .sort((a, b) => b.localeCompare(a))

  const filtered = svcJobs.filter(j => {
    const name = j.clients?.name || ''
    const matchSearch = !search || name.toLowerCase().includes(search.toLowerCase())
    const matchType = service === 'ia' || service === 'ote' || !filterType || j.audit_type === filterType
    const matchStatus = !filterStatus || jobStatusLabel(j) === filterStatus
    return inTab(j) && matchSearch && matchType && matchStatus
  })

  // Apply the active sort to the CURRENT filtered set (default next-deadline sort, or a manual column sort).
  const sorted = [...filtered].sort(sortCol ? manualCompare(sortCol, sortDir) : defaultCompare)

  function toggleSort(col) {
    if (sortCol !== col) { setSortCol(col); setSortDir('asc'); return }
    if (sortDir === 'asc') { setSortDir('desc'); return }
    setSortCol(null); setSortDir('asc')   // third click → back to the smart default
  }
  const sortArrow = col => (sortCol === col ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '')
  const thSort = { cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }

  // Status filter offers exactly the labels shown on the badges (and only those actually present), in
  // workflow order — the SAME displayState vocabulary as the column, never the raw workflow_state (which lumps
  // every approved-but-not-closed sub-state under one misleading "Pending Office Review").
  const STATUS_ORDER = ['Enquiry', 'Awaiting scheduling', 'Plan not sent', 'Scheduled', 'Audit in progress', 'Returned to auditor', 'Awaiting payment', 'Sending report', 'Awaiting close-out', 'In progress', 'Pending Office Review', 'Ready to Send Report', 'Awaiting Client Acknowledgement', 'Client Acknowledged', 'Ready for WSHC Submission', 'Pending WSHC Outcome', 'Returned for Revision', 'Completed', 'Ended – no certificate', 'Cancelled']
  const statusOptions = [...new Set(svcJobs.map(jobStatusLabel).filter(Boolean))]
    .sort((a, b) => { const ia = STATUS_ORDER.indexOf(a), ib = STATUS_ORDER.indexOf(b); return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib) })

  return (
    <div>
      {showNew && <NewJobModal ia={ia} onClose={() => setShowNew(false)} onCreated={j => { setJobs(js => [j, ...js]); setShowNew(false); navigate(`/jobs/${j.id}`) }} />}
      {showSAC && <SACReportModal jobs={jobs} onClose={() => setShowSAC(false)} />}
      <div className="page-header">
        <h1>Jobs</h1>
        {/* The switch sits alone at the far right so it never shifts when the buttons below change per service. */}
        <ServiceSwitch ote value={service} onChange={v => { setService(v); setFilterStatus(''); setFilterType('') }} />
      </div>
      {/* Tabs — same pattern as Quotations. Search (below) always looks across ALL tabs. */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        {[['active', `Active (${activeCount})`], ['completed', `Completed (${completedCount})`], ['all', `All (${svcJobs.length})`]].map(([v, l]) => (
          <button key={v} className={tab === v ? 'primary' : ''} onClick={() => { setTab(v); if (v !== 'completed') setYearFilter('') }}>{l}</button>
        ))}
        {tab === 'completed' && (
          <select value={yearFilter} onChange={e => setYearFilter(e.target.value)} style={{ width: 'auto' }} title="Filter completed jobs by audit year (for SAC evidence)">
            <option value="">All years</option>
            {completedYears.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        )}
        {search && tab !== 'all' && (
          <span style={{ fontSize: 13, color: 'var(--text3)' }}>searching all jobs, including completed</span>
        )}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginLeft: 'auto' }}>
          {!other && <button onClick={() => setShowSAC(true)}>SAC audit list</button>}
          {/* OTE jobs are created from a signed OTE quotation (Quotations → OTE). */}
          {!ote && <button className="primary" onClick={() => setShowNew(true)}>+ New job</button>}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
        <input placeholder="Search client…" value={search} onChange={e => setSearch(e.target.value)} style={{ maxWidth: 220 }} />
        {!other && <select value={filterType} onChange={e => setFilterType(e.target.value)} style={{ width: 'auto' }}>
          <option value="">All audit types</option>
          {Object.values(AUDIT_TYPES).filter(t => !isIA(t)).map(t => <option key={t} value={t}>{t}</option>)}
        </select>}
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ width: 'auto' }}>
          <option value="">All statuses</option>
          {statusOptions.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
      <div className="card" style={{ padding: 0 }}>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th style={thSort} onClick={() => toggleSort('client')} title="Sort by client name">Client{sortArrow('client')}</th>
                {ia && <th>Standard(s)</th>}
                <th>Status</th>
                <th style={thSort} onClick={() => toggleSort('audit')} title="Sort by audit date">Audit date{sortArrow('audit')}</th>
                {!other && <th style={thSort} onClick={() => toggleSort('expiry')} title="Sort by bizSAFE expiry">bizSAFE expiry{sortArrow('expiry')}</th>}
                <th>Auditor</th>
                {!ote && <th>Man-days</th>}
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={ia ? 7 : ote ? 5 : 7} style={{ color: 'var(--text3)', textAlign: 'center', padding: 24 }}>Loading…</td></tr>}
              {!loading && sorted.length === 0 && <tr><td colSpan={ia ? 7 : ote ? 5 : 7} style={{ color: 'var(--text3)', textAlign: 'center', padding: 24 }}>No jobs found</td></tr>}
              {sorted.map(j => (
                <tr key={j.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/jobs/${j.id}`)}>
                  <td style={{ fontWeight: 500 }}>{j.clients?.name || '—'}</td>
                  {/* bizSAFE: no Audit type / Scope columns (owner, 4 Nov 2026 — as clean as Internal Audit / OTE;
                      the "All audit types" filter above still narrows by type). */}
                  {ia && <td style={{ fontSize: 13, color: 'var(--text2)' }}>{standardsLabel((cycleStd && cycleStd[j.ia_cycle_id]) || j.form_data?.ia_standards || []) || '—'}</td>}
                  <td>{(() => {
                    // Linked (non-primary) member of a combined audit — the PRIMARY carries the whole workflow, so
                    // never show it as its own outstanding status (Enquiry / COI blocked / etc.). One combined
                    // audit = one workflow item; this row is covered.
                    if (j.combined_group_id && !j.is_combined_primary) {
                      return <span className="badge badge-info" style={{ whiteSpace: 'normal' }}>Combined audit (linked)</span>
                    }
                    // An unresolved high-risk COI is an outstanding action — never show plain "Scheduled".
                    if (!['Completed', 'Cancelled'].includes(j.status) && unresolvedHighRiskCoiNames(j, coiRows, coiDecs).length > 0) {
                      return <span className="badge badge-danger" style={{ whiteSpace: 'normal' }}>COI blocked</span>
                    }
                    // Internal audit: the client has the link but the PDF failed — open the job and "Prepare again".
                    if (pdfFailed.has(j.id)) return <span className="badge badge-danger" style={{ whiteSpace: 'normal' }}>Report PDF failed</span>
                    const lbl = jobStatusLabel(j)
                    return <span className={`badge ${STATUS_BADGE[lbl] || 'badge-gray'}`}>{lbl}</span>
                  })()}</td>
                  <td style={{ fontSize: 13 }}>{j.audit_date ? format(parseISO(j.audit_date), 'd MMM yyyy') : '—'}</td>
                  {!other && <td><ExpiryBadge date={resolveExpiry(j.clients, j.form_data)} /></td>}
                  <td style={{ fontSize: 13, color: 'var(--text2)' }}>{(ia ? [j.auditor_name, j.auditor2_name].filter(Boolean).join(', ') : j.auditor_name) || '—'}</td>
                  {!ote && <td style={{ fontSize: 13 }}>{j.mandays ? `${j.mandays}` : '—'}</td>}
                  <td style={{ whiteSpace: 'nowrap' }} onClick={e => e.stopPropagation()}>
                    <button className="sm" onClick={() => navigate(`/jobs/${j.id}`)} style={{ marginRight: 6 }}>View</button>
                    {!other && j.status !== 'Cancelled' && <button className="sm" onClick={() => navigate(`/jobs/${j.id}?edit=1`)}>Edit</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}